import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Produto } from '@/types';

interface CarrinhoItem {
  produto: Produto;
  quantidade: number;
}

interface CarrinhoState {
  items: CarrinhoItem[];
  cupomCodigo: string | null;
  desconto: number;
  
  // Actions
  adicionarItem: (produto: Produto, quantidade?: number) => void;
  removerItem: (produtoId: string) => void;
  atualizarQuantidade: (produtoId: string, quantidade: number) => void;
  limparCarrinho: () => void;
  aplicarCupom: (codigo: string, desconto: number) => void;
  removerCupom: () => void;
  
  // Getters
  totalItems: () => number;
  subtotal: () => number;
  total: () => number;
}

export const useCarrinhoStore = create<CarrinhoState>()(
  persist(
    (set, get) => ({
      items: [],
      cupomCodigo: null,
      desconto: 0,
      
      adicionarItem: (produto, quantidade = 1) => {
        set((state) => {
          const existingItem = state.items.find(
            (item) => item.produto.id === produto.id
          );
          
          if (existingItem) {
            return {
              items: state.items.map((item) =>
                item.produto.id === produto.id
                  ? { ...item, quantidade: item.quantidade + quantidade }
                  : item
              ),
            };
          }
          
          return {
            items: [...state.items, { produto, quantidade }],
          };
        });
      },
      
      removerItem: (produtoId) => {
        set((state) => ({
          items: state.items.filter((item) => item.produto.id !== produtoId),
        }));
      },
      
      atualizarQuantidade: (produtoId, quantidade) => {
        if (quantidade <= 0) {
          get().removerItem(produtoId);
          return;
        }
        
        set((state) => ({
          items: state.items.map((item) =>
            item.produto.id === produtoId ? { ...item, quantidade } : item
          ),
        }));
      },
      
      limparCarrinho: () => {
        set({ items: [], cupomCodigo: null, desconto: 0 });
      },
      
      aplicarCupom: (codigo, desconto) => {
        set({ cupomCodigo: codigo, desconto });
      },
      
      removerCupom: () => {
        set({ cupomCodigo: null, desconto: 0 });
      },
      
      totalItems: () => {
        return get().items.reduce((sum, item) => sum + item.quantidade, 0);
      },
      
      subtotal: () => {
        return get().items.reduce(
          (sum, item) => sum + item.produto.precoVenda * item.quantidade,
          0
        );
      },
      
      total: () => {
        const { desconto } = get();
        const subtotal = get().subtotal();
        return Math.max(0, subtotal - desconto);
      },
    }),
    {
      name: 'carrinho-storage',
    }
  )
);
